import { Box, IconButton, Skeleton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AdsClickIcon from '@mui/icons-material/AdsClick';
import PeopleIcon from '@mui/icons-material/People';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import Grid from '@mui/material/Grid2';
import { Link } from 'react-router-dom';
import { bodyWidth } from '../../../../utils/Style';
import { useLocalizedText } from '../../../../hooks/useLocalizedText';
import { GenericHelmet } from '../../../../components/Helmet/GenericHelmet';
import { AccountNavbar } from '../../AccountNavbar';
import { AffiliateUserSummary } from '../../../../interfaces/Affiliate/AffiliateUserSummary';
import {
  getAffiliateOrders,
  getAffiliateSummary,
} from '../../../../api/Affiliate';
import {
  DashboardStatsCard,
  DashboardStatsCardEmpty,
} from '../../../../components/Dashboard/DashboardStatsCard';
import { formatCurrency } from '../../../../utils/Currency';
import { PaginatedResponse } from '../../../../interfaces/PaginatedResponse';
import { MyOrder } from '../../../../interfaces/Order/MyOrder';
import { Pagination } from '../../../../interfaces/Pagination';
import { OrdersTable } from '../../../../components/Order/OrdersTable';

const cardSize = { xs: 12, md: 6, xl: 3 };

export const AffiliateProgramPage = () => {
  const t = useLocalizedText();

  const [orders, setOrders] = useState<PaginatedResponse<MyOrder>>({
    data: [],
    totalCount: 0,
  });
  const [summary, setSummary] = useState<AffiliateUserSummary | undefined>(
    undefined,
  );
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [orderPagination, setOrderPagination] = useState<Pagination>({
    page: 0,
    pageSize: 10,
  });

  const refreshOrders = () => {
    setLoadingOrders(true);
    getAffiliateOrders(orderPagination)
      .then(setOrders)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingOrders(false));
  };

  useEffect(() => {
    setLoadingSummary(true);
    getAffiliateSummary()
      .then(setSummary)
      .catch((err) => toast.error(err))
      .finally(() => setLoadingSummary(false));
  }, []);

  useEffect(() => {
    refreshOrders();
  }, [orderPagination]);

  const handleCopyAffiliateCode = () => {
    navigator.clipboard
      .writeText(summary?.affiliateCode ?? '')
      .then(() =>
        toast.success(
          t('affiliate_program.affiliate_program_page.code_copied'),
        ),
      );
  };

  const affiliateLink = `${window.location.origin}/?aff=${summary?.affiliateCode}`;
  const handleCopyAffiliateLink = () => {
    navigator.clipboard
      .writeText(affiliateLink)
      .then(() =>
        toast.success(
          t('affiliate_program.affiliate_program_page.link_copied'),
        ),
      );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
      }}
    >
      <GenericHelmet
        title={t('affiliate_program.affiliate_program_page.title')}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: bodyWidth,
          pb: 3,
          px: 1.5,
          gap: 1.5,
          boxSizing: 'border-box',
        }}
      >
        <AccountNavbar selectedTab='partner-program' />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              sx={{ fontWeight: 600, textWrap: 'none', whiteSpace: 'nowrap' }}
            >
              {t(
                'affiliate_program.affiliate_program_page.your_affiliate_code',
              )}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                gap: 1,
              }}
            >
              {loadingSummary ? (
                <Skeleton variant='rectangular' height={20} width={150} />
              ) : (
                <Typography sx={{ lineHeight: '14px' }}>
                  {summary?.affiliateCode}
                </Typography>
              )}

              {!!summary?.affiliateCode && (
                <IconButton
                  color='primary'
                  size='small'
                  onClick={handleCopyAffiliateCode}
                  sx={{ p: 0, width: 'fit-content', height: 'fit-content' }}
                >
                  <ContentCopyIcon sx={{ width: 14, height: 14 }} />
                </IconButton>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              sx={{ fontWeight: 600, textWrap: 'none', whiteSpace: 'nowrap' }}
            >
              {t(
                'affiliate_program.affiliate_program_page.your_affiliate_link',
              )}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                gap: 1,
              }}
            >
              {loadingSummary ? (
                <Skeleton variant='rectangular' height={20} width={150} />
              ) : (
                <Link to={affiliateLink}>{affiliateLink}</Link>
              )}

              {!!summary?.affiliateCode && (
                <IconButton
                  color='primary'
                  size='small'
                  onClick={handleCopyAffiliateLink}
                  sx={{ p: 0, width: 'fit-content', height: 'fit-content' }}
                >
                  <ContentCopyIcon sx={{ width: 14, height: 14 }} />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
        <Grid container spacing={3} mt={1.5}>
          {loadingSummary ? (
            <>
              <Grid size={cardSize}>
                <DashboardStatsCardEmpty />
              </Grid>
              <Grid size={cardSize}>
                <DashboardStatsCardEmpty />
              </Grid>
              <Grid size={cardSize}>
                <DashboardStatsCardEmpty />
              </Grid>
              <Grid size={cardSize}>
                <DashboardStatsCardEmpty />
              </Grid>
            </>
          ) : (
            <>
              <Grid size={cardSize}>
                <DashboardStatsCard
                  title={t(
                    'affiliate_program.affiliate_program_page.total_visit',
                  )}
                  icon={<AdsClickIcon fontSize='large' color='success' />}
                  value={(summary?.totalClicks ?? 0).toString()}
                />
              </Grid>
              <Grid size={cardSize}>
                <DashboardStatsCard
                  title={t(
                    'affiliate_program.affiliate_program_page.unique_visitors',
                  )}
                  icon={
                    <PeopleIcon fontSize='large' sx={{ color: '#f27a8d' }} />
                  }
                  value={(summary?.totalUniqueClicks ?? 0).toString()}
                />
              </Grid>
              <Grid size={cardSize}>
                <DashboardStatsCard
                  title={t(
                    'affiliate_program.affiliate_program_page.total_orders',
                  )}
                  icon={
                    <ShoppingCartIcon
                      fontSize='large'
                      sx={{ color: '#6589e2' }}
                    />
                  }
                  value={(summary?.totalOrders ?? 0).toString()}
                />
              </Grid>
              <Grid size={cardSize}>
                <DashboardStatsCard
                  title={t(
                    'affiliate_program.affiliate_program_page.total_revenue',
                  )}
                  tooltip={t(
                    'affiliate_program.affiliate_program_page.total_revenue_tooltip',
                  )}
                  icon={
                    <CurrencyExchangeIcon
                      fontSize='large'
                      sx={{ color: '#9499d8' }}
                    />
                  }
                  value={formatCurrency(summary?.totalRevenue ?? 0)}
                />
              </Grid>
            </>
          )}
        </Grid>

        <Typography
          sx={{
            fontWeight: 600,
            textWrap: 'none',
            whiteSpace: 'nowrap',
            mt: 1.5,
          }}
        >
          {t('account.page.recent_orders')}
        </Typography>
        <OrdersTable
          orders={orders.data}
          totalCount={orders.totalCount}
          pagination={orderPagination}
          onPagination={setOrderPagination}
          loading={loadingOrders}
          readOnly
        />
      </Box>
    </Box>
  );
};
